// Guest cart helper — localStorage-backed cart for anonymous visitors.
// The cart lives entirely in the browser until the user logs in / signs up,
// at which point `mergeGuestCart(api)` posts each item to the authenticated
// cart API and clears the local store.
//
// LocalStorage keys (per spec):
//   steward_guest_cart        [{ course_id, course_title, thumbnail_url,
//                                base_price, discount_price, added_at }]
//   steward_applied_coupon    string (uppercased coupon code)
//   steward_checkout_redirect string ("1" if we should route to /cart after login)

const K_CART = "steward_guest_cart";
const K_COUPON = "steward_applied_coupon";
const K_REDIRECT = "steward_checkout_redirect";

const CART_UPDATED_EVENT = "cart:updated";

function readCart() {
  try {
    return JSON.parse(localStorage.getItem(K_CART) || "[]");
  } catch { return []; }
}

function writeCart(items) {
  localStorage.setItem(K_CART, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
}

export function getGuestCart() {
  return readCart();
}

export function getGuestCartCount() {
  return readCart().length;
}

export function getAppliedCoupon() {
  return localStorage.getItem(K_COUPON) || "";
}

export function setAppliedCoupon(code) {
  if (code) localStorage.setItem(K_COUPON, code.toUpperCase());
  else localStorage.removeItem(K_COUPON);
  window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
}

export function addToGuestCart(course) {
  if (!course?.id) return { added: false, reason: "invalid_course" };
  const items = readCart();
  if (items.some((i) => i.course_id === course.id)) {
    return { added: false, reason: "duplicate" };
  }
  items.push({
    course_id: course.id,
    course_title: course.course_title,
    thumbnail_url: course.thumbnail_url || "",
    slug: course.slug || course.course_code || "",
    base_price: Number(course.base_price || 0),
    discount_price: Number(course.discount_price || course.base_price || 0),
    added_at: new Date().toISOString(),
  });
  writeCart(items);
  return { added: true };
}

export function removeFromGuestCart(courseId) {
  writeCart(readCart().filter((i) => i.course_id !== courseId));
}

export function clearGuestCart() {
  localStorage.removeItem(K_CART);
  localStorage.removeItem(K_COUPON);
  window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
}

export function setCheckoutRedirect(on = true) {
  if (on) localStorage.setItem(K_REDIRECT, "1");
  else localStorage.removeItem(K_REDIRECT);
}

export function shouldRedirectToCheckout() {
  return localStorage.getItem(K_REDIRECT) === "1";
}

/**
 * Merge the guest cart into the authenticated user's cart.
 * Uses the existing POST /api/learner/cart/add-course endpoint.
 *
 * Returns { merged, skipped_duplicates, already_owned, failures }
 */
export async function mergeGuestCart(api) {
  const items = readCart();
  const coupon = getAppliedCoupon();
  if (!items.length) return { merged: 0, skipped_duplicates: 0, already_owned: 0, failures: 0, coupon };

  let merged = 0;
  let skipped_duplicates = 0;
  let already_owned = 0;
  let failures = 0;

  for (const it of items) {
    try {
      await api.post("/learner/cart/add-course", { course_id: it.course_id });
      merged += 1;
    } catch (err) {
      const msg = (err?.response?.data?.detail || err?.message || "").toString().toLowerCase();
      if (msg.includes("already in cart") || msg.includes("already exists")) {
        skipped_duplicates += 1;
      } else if (msg.includes("already purchased") || msg.includes("already owned") || msg.includes("already enrolled")) {
        already_owned += 1;
      } else {
        failures += 1;
      }
    }
  }
  // Clear guest cart on success (keep coupon for cart page to re-apply)
  localStorage.removeItem(K_CART);
  window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
  return { merged, skipped_duplicates, already_owned, failures, coupon };
}
